import { Box, FormControlLabel, Stack, Switch, TextField, ToggleButton, ToggleButtonGroup, Typography, switchClasses } from "@mui/material";
import React from "react";
import CustomTooltip from "@hyro-components/Tooltip";
import QuestionIcon from "src/icons/insights/QuestionIcon";
import { LATEST_TAG, NLU_MODES, FIELD_IDS } from "../DeployWizard.consts";

const ServiceField = ({
  id,
  label,
  value,
  isLatest,
  onValueChange,
  onLatestToggle,
  error,
  helperText,
  disabled,
  showLatestToggle = true,
  modeSelector = false,
  extraFields = [],
  formData = {},
  errors = {},
  onFieldChange,
  required = false,
  subtitle
}) => {
  const selectedNluMode = formData[FIELD_IDS.NLU_MODE] || (modeSelector ? NLU_MODES.API : "");
  const visibleExtraFields = modeSelector && selectedNluMode !== NLU_MODES.DEDICATED
    ? []
    : extraFields;

  const displayLabel = modeSelector && selectedNluMode
    ? `NLU Runtime (${selectedNluMode === NLU_MODES.DEDICATED ? "Dedicated" : "API"} mode)`
    : label;

  return (
    <Stack gap={1}>
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="subtitle2" color="text.700">{displayLabel}</Typography>
          {required && <Typography variant="subtitle2" color="error.main">*</Typography>}
        </Stack>
        {modeSelector ? (
          <ToggleButtonGroup
            value={selectedNluMode}
            exclusive
            size="small"
            onChange={(_, newMode) => {
              if (newMode !== null) onFieldChange(FIELD_IDS.NLU_MODE, newMode);
            }}
            sx={{
              height: 28,
              "& .MuiToggleButton-root": {
                textTransform: "none",
                fontSize: 12,
                fontWeight: 500,
                px: 1.5,
                py: 0,
                color: "text.700",
                "&.Mui-selected": {
                  backgroundColor: "tertiary.100",
                  color: "primary.600",
                  "&:hover": { backgroundColor: "tertiary.200" }
                }
              }
            }}
          >
            <ToggleButton value={NLU_MODES.API}>API</ToggleButton>
            <ToggleButton value={NLU_MODES.DEDICATED}>Dedicated</ToggleButton>
          </ToggleButtonGroup>
        ) : showLatestToggle && (
          <FormControlLabel
            control={
              <Switch
                size="small"
                sx={{ ml: 1, [`& .${switchClasses.thumb}`]: { boxShadow: (theme) => theme.shadows[4] } }}
                checked={isLatest}
                onChange={(e) => onLatestToggle(id, e.target.checked)}
                disabled={disabled}
              />
            }
            label={<Typography variant="caption">Latest</Typography>}
          />
        )}
      </Stack>
      {modeSelector && showLatestToggle && (
        <Stack direction="row" justifyContent="flex-end">
          <FormControlLabel
            control={
              <Switch
                size="small"
                sx={{ ml: 1, [`& .${switchClasses.thumb}`]: { boxShadow: (theme) => theme.shadows[4] } }}
                checked={isLatest}
                onChange={(e) => onLatestToggle(id, e.target.checked)}
                disabled={disabled}
              />
            }
            label={<Typography variant="caption">Latest</Typography>}
          />
        </Stack>
      )}
      {subtitle && (
        <Typography variant="caption" color="text.600">{subtitle}</Typography>
      )}
      <TextField
        size="small"
        name={id}
        placeholder={isLatest ? LATEST_TAG : "Commit SHA"}
        value={isLatest ? "" : value}
        onChange={(e) => onValueChange(id, e.target.value)}
        disabled={disabled || isLatest}
        error={!!error}
        helperText={helperText}
        fullWidth
      />
      {visibleExtraFields.length > 0 && (
        <Box sx={{ pl: 2, borderLeft: "2px solid", borderColor: "grey.300", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
          {visibleExtraFields.map((field) =>
            field.inputType === "switch" ? (
              <FormControlLabel
                key={field.id}
                sx={{ ml: 0 }}
                control={
                  <Switch
                    sx={{ ml: 1, [`& .${switchClasses.thumb}`]: { boxShadow: (theme) => theme.shadows[4] } }}
                    size="small"
                    checked={!!formData[field.id]}
                    onChange={(e) => onFieldChange(field.id, e.target.checked)}
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="caption">{field.label}</Typography>
                    {field.tooltip && (
                      <CustomTooltip title={field.tooltip}>
                        <Stack alignItems="center">
                          <QuestionIcon />
                        </Stack>
                      </CustomTooltip>
                    )}
                  </Stack>
                }
              />
            ) : (
              <TextField
                key={field.id}
                size="small"
                name={field.id}
                label={field.label}
                value={formData[field.id] || ""}
                onChange={(e) => onFieldChange(field.id, e.target.value)}
                error={!!errors[field.id]}
                helperText={errors[field.id] || ""}
                type={field.inputType === "number" ? "number" : "text"}
                inputProps={field.inputType === "number" ? { min: 1, max: 10 } : undefined}
                required={field.inputType !== "number"}
                fullWidth
              />
            )
          )}
        </Box>
      )}
    </Stack>
  );
};

export default ServiceField;
